import React, { useContext, useState, useEffect, Fragment, useCallback, useMemo } from 'react';
import Layout from '../../components/layout/main';
import { makeStyles } from 'tss-react/mui';
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Fade, Grid, Paper, Stack, Typography } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PlaceIcon from '@mui/icons-material/Place';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import BadgeIcon from '@mui/icons-material/Badge';
import * as yup from 'yup';
import { postData, getData, putData, getDataFull } from '../../services/ajax';
import { AuthContext } from '../../context/auth';
import { LoadingModalContext } from '../../components/modal/loading';
import { AlertModalContext } from '../../components/modal/alert';
import { setYupLocaleES } from '../../const/yup';
import { useNavigate } from 'react-router-dom';
import { CustomForm } from '../../components/formSections';
import ProcessGuide from '../../components/orden/ProcessGuide';
import { GlobalsContext } from '../../context/globals';
import { useSnackbar } from 'notistack';
import GeoDireccionForm, {
    emptyGeoDireccion,
    geoDireccionFromApi,
    geoDireccionToApi,
} from '../../components/geo/GeoDireccionForm';
import { FormSectionBlock, FormSectionHeader } from '../../components/ui/FormSection';

setYupLocaleES();

const useStyles = makeStyles()((theme) => ({
    paper: {
        padding: theme.spacing(3),
        paddingBottom: theme.spacing(2),
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
        width: '100%',
        marginBottom: theme.spacing(2),
    },
}));

function Page() {
    const { classes } = useStyles();
    const theme = useTheme();
    const { usuario } = useContext(AuthContext);
    const navigate = useNavigate();
    const globalsContext = useContext(GlobalsContext);
    const [data, setData] = useState(null);
    const [practicasList, setPracticasList] = useState([]);
    const [geoState, setGeoState] = useState(emptyGeoDireccion());

    const loadingModal = useContext(LoadingModalContext);
    const alertModal = useContext(AlertModalContext);
    const { enqueueSnackbar } = useSnackbar();

    const isEdit = Boolean(data?.id);
    const pageTitle = isEdit ? 'Editar establecimiento' : 'Nuevo establecimiento';
    const accent = theme.palette.primary;

    useEffect(() => {
        const loadPracticas = async () => {
            const response = await getDataFull('practicas?per_page=200', usuario.token);
            if (response.error === '' && response.data) setPracticasList(response.data);
        };
        loadPracticas();
    }, [usuario.token]);

    const onSubmit = async (values) => {
        const geo_direccion = geoDireccionToApi(geoState);
        if (!geo_direccion) {
            enqueueSnackbar(
                'Corresponde completar el domicilio (localidad, ubicación en mapa y, si aplica, calle y altura).',
                { variant: 'error' }
            );
            return;
        }

        loadingModal.setOpen(true);
        try {
            const practicaIds = Array.isArray(values.practicas_detalle)
                ? [...new Set(values.practicas_detalle.map((r) => Number(r.practica_id)))]
                : [];
            const payload = {
                nombre: values.nombre,
                tipo: values.tipo,
                activo: Boolean(values.activo),
                geo_direccion,
                practica_ids: practicaIds,
            };
            const response = data && data.id
                ? await putData(`establecimientos/${data.id}`, payload, usuario.token)
                : await postData('establecimientos', payload, usuario.token);
            if (response.error === '') {
                enqueueSnackbar(
                    data ? 'Establecimiento modificado correctamente' : 'Establecimiento creado correctamente',
                    { variant: 'success' }
                );
                onClose();
            } else {
                enqueueSnackbar(response.error, { variant: 'error' });
            }
        } catch (error) {
            enqueueSnackbar('Error al guardar el establecimiento', { variant: 'error' });
        } finally {
            loadingModal.setOpen(false);
        }
    };

    const onClose = useCallback(() => navigate(-1), [navigate]);

    const onCancel = () => {
        alertModal.showModal('Cancelar', 'Se perderán todos los cambios. ¿Desea continuar?', () => onClose());
    };

    const loadData = useCallback(async () => {
        if (!globalsContext.idEdit) return;
        loadingModal.setOpen(true);
        try {
            const response = await getData(`establecimientos/${globalsContext.idEdit}`, usuario.token);
            if (response.error === '') {
                setData(response.data);
                setGeoState(geoDireccionFromApi(response.data.geo_direccion) || emptyGeoDireccion());
            } else {
                enqueueSnackbar(response.error, { variant: 'error' });
            }
        } catch (error) {
            enqueueSnackbar('Error al cargar el establecimiento', { variant: 'error' });
        } finally {
            loadingModal.setOpen(false);
        }
    }, [globalsContext.idEdit, usuario.token]);

    useEffect(() => {
        if (globalsContext.idEdit) loadData();
    }, [globalsContext.idEdit, loadData]);

    const practicaItems = useMemo(() => practicasList.map((p) => ({ id: p.id, nombre: p.nombre })), [practicasList]);

    const formSections = useMemo(() => [
        {
            name: 'identidad',
            hideTitle: true,
            description: '',
            fields: [
                {
                    dataName: 'nombre',
                    label: 'Nombre',
                    controlType: 'textField',
                    initialValue: data ? data.nombre : '',
                    validationSchema: yup.string().required('El nombre es requerido').max(255, 'Máximo 255 caracteres'),
                    autoFocus: true,
                    helperText: 'Nombre con el que se identifica el centro en el sistema.',
                },
                {
                    dataName: 'tipo',
                    label: 'Tipo',
                    controlType: 'select',
                    items: [
                        { id: 'publico', nombre: 'Público' },
                        { id: 'privado', nombre: 'Privado' },
                    ],
                    initialValue: data ? data.tipo : 'publico',
                    validationSchema: yup.string().required('El tipo es requerido').oneOf(['publico', 'privado']),
                    helperText: 'Público: permite cargar órdenes al sistema. Privado: solo responde órdenes; no carga pedidos.',
                },
                {
                    dataName: 'activo',
                    label: 'Activo',
                    controlType: 'checkBox',
                    initialValue: data ? data.activo !== false : true,
                    validationSchema: yup.boolean(),
                    helperText: 'Si está desactivado, el centro no participa en autoasignación ni altas nuevas.',
                },
            ],
        },
        {
            name: 'practicas',
            hideTitle: true,
            description: '',
            fields: [
                {
                    dataName: 'practicas_detalle',
                    label: 'Prácticas que realiza el establecimiento',
                    controlType: 'detalleList',
                    initialValue: data && data.practica_ids ? data.practica_ids.map((id) => ({ practica_id: id })) : [],
                    listFields: [
                        {
                            dataName: 'practica_id',
                            label: 'Práctica',
                            controlType: 'select',
                            items: practicaItems,
                            visibleType: 1,
                            initialValue: '',
                            validationSchema: yup.number().required('Seleccione una práctica'),
                        },
                    ],
                    labelPopup: 'práctica',
                    labelPopupGenero: 'a',
                    validationSchema: yup.array(),
                    helperText: 'Solo las prácticas asociadas pueden asignarse a este centro.',
                },
            ],
        },
    ], [data, practicaItems]);

    const formInitialValues = useMemo(() => ({
        nombre: data ? data.nombre : '',
        tipo: data ? data.tipo : 'publico',
        activo: data ? data.activo !== false : true,
        practicas_detalle: data && data.practica_ids ? data.practica_ids.map((id) => ({ practica_id: id })) : [],
    }), [data]);

    const renderAfterSection = useCallback((sectionIndex) => {
        if (sectionIndex !== 0) return null;
        return (
            <FormSectionBlock
                accentColor="info"
                icon={PlaceIcon}
                title="Domicilio"
                description="La ubicación se usa en la autoasignación. Con “Sin calle”, corresponde marcar el punto en el mapa."
            >
                <GeoDireccionForm
                    token={usuario.token}
                    value={geoState}
                    onChange={setGeoState}
                    required
                    title=""
                />
            </FormSectionBlock>
        );
    }, [usuario.token, geoState]);

    return (
        <Fragment>
            <Layout nombrePagina={pageTitle}>
                <Fade in={true} timeout={900}>
                    <Grid size={12}>
                        <Box
                            sx={{
                                mb: 2,
                                borderRadius: 2,
                                border: `1px solid ${theme.palette.divider}`,
                                overflow: 'hidden',
                                background: `linear-gradient(135deg, ${alpha(accent.main, 0.08)} 0%, ${alpha(accent.main, 0.02)} 55%, transparent 100%)`,
                            }}
                        >
                            <Box
                                sx={{
                                    height: 5,
                                    background: `linear-gradient(90deg, ${accent.dark || accent.main} 0%, ${alpha(accent.main, 0.55)} 55%, transparent 100%)`,
                                }}
                            />
                            <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                                <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
                                    <Box
                                        sx={{
                                            width: 52,
                                            height: 52,
                                            borderRadius: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            bgcolor: alpha(accent.main, 0.18),
                                            color: accent.main,
                                        }}
                                    >
                                        <BusinessIcon fontSize="medium" />
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
                                            {pageTitle}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 720 }}>
                                            Permite registrar un centro para la carga o respuesta de órdenes, con domicilio para autoasignación y prácticas asociadas.
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>
                        </Box>

                        <ProcessGuide
                            title="Cómo completar el establecimiento"
                            steps={[
                                'Definir identidad del centro (tipo y estado activo).',
                                'Completar el domicilio; si no hay geolocalización automática, corresponde ubicar el pin en el mapa.',
                                'Asociar las prácticas que realiza el establecimiento.',
                            ]}
                        />

                        <Paper className={classes.paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                            <FormSectionHeader
                                accentColor="primary"
                                icon={BadgeIcon}
                                title="Identidad"
                                description="Datos básicos del centro y su disponibilidad operativa."
                            />
                            <CustomForm
                                key={data ? `e-${data.id}` : 'e-new'}
                                formSections={formSections}
                                initialValues={formInitialValues}
                                onSubmit={onSubmit}
                                onCancel={onCancel}
                                submitButtonText={data ? 'Guardar cambios' : 'Crear establecimiento'}
                                cancelButtonText="Cancelar"
                                stepperType={false}
                                isModal={false}
                                renderAfterSection={(sectionIndex) => {
                                    if (sectionIndex === 0) {
                                        return (
                                            <>
                                                {renderAfterSection(0)}
                                                <FormSectionHeader
                                                    accentColor="secondary"
                                                    icon={MedicalServicesIcon}
                                                    title="Prácticas"
                                                    description="Solo las prácticas asociadas pueden asignarse a este centro."
                                                />
                                            </>
                                        );
                                    }
                                    return null;
                                }}
                            />
                        </Paper>
                    </Grid>
                </Fade>
            </Layout>
        </Fragment>
    );
}

export default Page;
